import cronstrue from 'cronstrue/i18n';
import _ from 'lodash';
import { quartzCronExpressionHelper } from '@/annotation/reportContent.utilities';
import { sqWorkbenchStore } from '@/core/core.stores';
import { MAX_DAY, MAX_MONTH, MIN_DAY, MIN_MONTH } from '@/schedule/schedule.constants';
import {
  DailyScheduleType,
  MonthlyScheduleData,
  MonthlyScheduleTypeName,
  ScheduleTypeName,
  WeeklyScheduleData,
} from '@/schedule/schedule.types';
import { LOCALES } from '@/utilities/i18n.constants';
import type {
  AutoRate,
  AutoUpdateTimeScheduleEntry,
  LiveUpdateRate,
  ScheduleType,
  UpdatedSchedule,
} from '@/schedule/schedule.types';

interface TimeEntryValidationOptions {
  minimumIntervalMinutes?: number;
  selectedScheduleType?: ScheduleType;
}

const getCronstrueLocale = (): string => {
  const userLanguage = sqWorkbenchStore.userLanguage;
  // cronstrue uses underscores for these locales; for everything else the first two characters are accepted.
  // See https://www.npmjs.com/package/cronstrue
  switch (userLanguage) {
    case LOCALES.ZH:
    case LOCALES.PT:
      return userLanguage.replace('-', '_');
    default:
      return userLanguage.substring(0, 2);
  }
};

const formatCronDescription = (cron: unknown, dayOfWeekStartIndexZero: boolean): string | undefined => {
  if (typeof cron !== 'string' || !cron.trim()) {
    return undefined;
  }
  try {
    return cronstrue.toString(cron, { dayOfWeekStartIndexZero, locale: getCronstrueLocale() });
  } catch {
    return undefined;
  }
};

/** Formats standard cron expressions, where day-of-week 0 is Sunday and 1 is Monday. */
export const getStandardCronDescription = (cron: unknown): string | undefined => formatCronDescription(cron, true);

/** Formats Quartz cron expressions, where day-of-week 1 is Sunday and 2 is Monday. */
export const getQuartzCronDescription = (cron: unknown): string | undefined => formatCronDescription(cron, false);

// Preserve the standard-cron behavior of the existing public helper for its existing callers.
export const getCronDescription = getStandardCronDescription;

const getCronScheduleDescriptionFor = (
  crons: string[] | undefined,
  formatter: (cron: unknown) => string | undefined,
): string | undefined => {
  if (!crons || crons.length === 0) {
    return undefined;
  }
  const descriptions = _.compact(_.map(crons, formatter));
  return descriptions.length > 0 ? descriptions.join('; ') : undefined;
};

export const getCronScheduleDescription = (crons: string[] | undefined): string | undefined =>
  getCronScheduleDescriptionFor(crons, getStandardCronDescription);

export const getQuartzCronScheduleDescription = (crons: string[] | undefined): string | undefined =>
  getCronScheduleDescriptionFor(crons, getQuartzCronDescription);

export const defaultScheduleType: () => ScheduleType = () => ({
  selectedType: ScheduleTypeName.DAILY,
  data: {
    [ScheduleTypeName.DAILY]: DailyScheduleType.EVERY_DAY,
    [ScheduleTypeName.WEEKLY]: new WeeklyScheduleData(),
    [ScheduleTypeName.MONTHLY]: new MonthlyScheduleData(),
  },
});

/**
 * Helper function to assert that all of a given array of values are numbers.
 *
 * @param values - The values to check
 *
 * @returns - true if all the values are finite and numeric; false otherwise
 */
export const areAllElementsANumber = (values: string[]): boolean => _.every(values, (el) => _.isFinite(_.toNumber(el)));

/**
 * Determine the times at which updates based on this schedule will be run.
 *
 * @param minutes - An array of the minutes specified by the cron schedule
 * @param hours - An array of the hours specified by the cron schedule
 *
 * @returns - corresponding to the source cron schedule
 * @throws {Error} if there's an error parsing the schedule
 */
export function determineTimeEntries(minutes: string[], hours: string[]): AutoUpdateTimeScheduleEntry[] {
  const results: string[] = [];
  if (!areAllElementsANumber(minutes) || !areAllElementsANumber(hours)) {
    throw new Error('Unable to parse the schedule');
  }

  _.forEach(hours, (hour) => {
    const paddedHour = hour.padStart(2, '0');
    _.forEach(minutes, (minute) => {
      const paddedMinute = minute.padStart(2, '0');
      results.push(`${paddedHour}:${paddedMinute}`);
    });
  });

  // If we support more strategies in the future, we will have to modify this
  return _.map(results, (time) => ({ time }));
}

/**
 * Return a Quartz cron schedule for a {@link ScheduleType} object describing the auto update schedule.
 *
 * @param schedule - The auto update schedule for which a cron expression is desired
 * @param timeEntry - The time to schedule
 * @returns - The cron schedule
 */
export function entriesToQuartzCronExpression(
  schedule: ScheduleType,
  timeEntry: AutoUpdateTimeScheduleEntry,
): string | undefined {
  if (_.isEmpty(schedule)) {
    return;
  }

  const quartzCronExpressionHelperInstance = quartzCronExpressionHelper();
  const DAYS_OF_THE_WEEK_TO_CRON = {
    sunday: quartzCronExpressionHelperInstance.CRON_DAYS_OF_WEEK.SUN,
    monday: quartzCronExpressionHelperInstance.CRON_DAYS_OF_WEEK.MON,
    tuesday: quartzCronExpressionHelperInstance.CRON_DAYS_OF_WEEK.TUE,
    wednesday: quartzCronExpressionHelperInstance.CRON_DAYS_OF_WEEK.WED,
    thursday: quartzCronExpressionHelperInstance.CRON_DAYS_OF_WEEK.THU,
    friday: quartzCronExpressionHelperInstance.CRON_DAYS_OF_WEEK.FRI,
    saturday: quartzCronExpressionHelperInstance.CRON_DAYS_OF_WEEK.SAT,
    SUNDAY: quartzCronExpressionHelperInstance.CRON_DAYS_OF_WEEK.SUN,
    MONDAY: quartzCronExpressionHelperInstance.CRON_DAYS_OF_WEEK.MON,
    TUESDAY: quartzCronExpressionHelperInstance.CRON_DAYS_OF_WEEK.TUE,
    WEDNESDAY: quartzCronExpressionHelperInstance.CRON_DAYS_OF_WEEK.WED,
    THURSDAY: quartzCronExpressionHelperInstance.CRON_DAYS_OF_WEEK.THU,
    FRIDAY: quartzCronExpressionHelperInstance.CRON_DAYS_OF_WEEK.FRI,
    SATURDAY: quartzCronExpressionHelperInstance.CRON_DAYS_OF_WEEK.SAT,
  };

  const { selectedType, data } = schedule;
  const times = [timeEntry.time];

  if (selectedType === ScheduleTypeName.DAILY) {
    return data[ScheduleTypeName.DAILY] === DailyScheduleType.EVERY_DAY
      ? quartzCronExpressionHelperInstance.createDailySchedule(times)
      : quartzCronExpressionHelperInstance.createWeekdaySchedule(times);
  } else if (selectedType === ScheduleTypeName.WEEKLY) {
    const { sunday, monday, tuesday, wednesday, thursday, friday, saturday } = data[ScheduleTypeName.WEEKLY];
    const daysOfWeek = [];

    if (sunday) daysOfWeek.push(DAYS_OF_THE_WEEK_TO_CRON.sunday);
    if (monday) daysOfWeek.push(DAYS_OF_THE_WEEK_TO_CRON.monday);
    if (tuesday) daysOfWeek.push(DAYS_OF_THE_WEEK_TO_CRON.tuesday);
    if (wednesday) daysOfWeek.push(DAYS_OF_THE_WEEK_TO_CRON.wednesday);
    if (thursday) daysOfWeek.push(DAYS_OF_THE_WEEK_TO_CRON.thursday);
    if (friday) daysOfWeek.push(DAYS_OF_THE_WEEK_TO_CRON.friday);
    if (saturday) daysOfWeek.push(DAYS_OF_THE_WEEK_TO_CRON.saturday);

    return quartzCronExpressionHelperInstance.createWeeklySchedule(daysOfWeek, times);
  } else if (selectedType === ScheduleTypeName.MONTHLY) {
    const typeOfMonthly = data[ScheduleTypeName.MONTHLY].selectedType;
    if (typeOfMonthly === MonthlyScheduleTypeName.BY_DAY_OF_MONTH) {
      const subData = data[ScheduleTypeName.MONTHLY].data[MonthlyScheduleTypeName.BY_DAY_OF_MONTH];
      return quartzCronExpressionHelperInstance.createMonthlyScheduleByDayOfMonth(
        subData.day,
        subData.numberOfMonths,
        times,
      );
    } else if (typeOfMonthly === MonthlyScheduleTypeName.BY_DAY_OF_WEEK) {
      const subData = data[ScheduleTypeName.MONTHLY].data[MonthlyScheduleTypeName.BY_DAY_OF_WEEK];
      return quartzCronExpressionHelperInstance.createMonthlyScheduleByDayOfWeek(
        subData.nth,
        DAYS_OF_THE_WEEK_TO_CRON[subData.dayOfWeek],
        subData.numberOfMonths,
        times,
      );
    } else {
      throw new Error('Cannot map cron expression due to unknown monthly schedule');
    }
  } else if (selectedType === ScheduleTypeName.LIVE) {
    // do nothing
  } else {
    throw new Error('Cannot map cron expression due to unknown schedule');
  }
}

export const isDailyScheduleValid = (dailyScheduleType?: DailyScheduleType) =>
  _.includes(Object.values(DailyScheduleType), dailyScheduleType);

export const isWeeklyScheduleValid = ({
  monday,
  tuesday,
  wednesday,
  thursday,
  friday,
  saturday,
  sunday,
}: WeeklyScheduleData) => monday || tuesday || wednesday || thursday || friday || saturday || sunday;

export const isMonthlyScheduleValid = ({ selectedType, data }: MonthlyScheduleData) => {
  if (_.isNil(selectedType)) {
    return false;
  }

  if (selectedType === MonthlyScheduleTypeName.BY_DAY_OF_MONTH) {
    const { numberOfMonths, day } = data[MonthlyScheduleTypeName.BY_DAY_OF_MONTH];

    return (
      _.isFinite(numberOfMonths) &&
      numberOfMonths >= MIN_MONTH &&
      numberOfMonths <= MAX_MONTH &&
      _.isFinite(day) &&
      day >= MIN_DAY &&
      day <= MAX_DAY
    );
  }

  const { numberOfMonths } = data[MonthlyScheduleTypeName.BY_DAY_OF_WEEK];

  return _.isFinite(numberOfMonths) && numberOfMonths >= MIN_MONTH && numberOfMonths <= MAX_MONTH;
};

const MINUTES_PER_DAY = 24 * 60;
const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY;
const LIVE_RATE_MINUTES_BY_UNIT: Partial<Record<string, number>> = {
  s: 1 / 60,
  min: 1,
  h: 60,
  day: MINUTES_PER_DAY,
};

const getEntryMinutes = ({ time }: AutoUpdateTimeScheduleEntry): number => {
  const [hours, minutes] = time.split(':').map(_.toInteger);
  return hours * 60 + minutes;
};

const getLiveRateMinutes = (liveRate: LiveUpdateRate): number => {
  const unit = liveRate.unit.unit[0];
  const multiplier = unit ? LIVE_RATE_MINUTES_BY_UNIT[unit] : undefined;
  if (!multiplier || !Number.isFinite(liveRate.value) || liveRate.value <= 0) {
    return 0;
  }

  return liveRate.value * multiplier;
};

const getRepeatingWeekdays = (scheduleType: ScheduleType): number[] | undefined => {
  const { selectedType, data } = scheduleType;

  if (selectedType === ScheduleTypeName.DAILY) {
    return data[ScheduleTypeName.DAILY] === DailyScheduleType.EVERY_DAY ? [0, 1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5];
  }

  if (selectedType === ScheduleTypeName.WEEKLY) {
    const weeklyData = data[ScheduleTypeName.WEEKLY];
    // Pair each weekday flag with its index and filter; _.compact would drop Sunday (0) as a
    // falsy value, silently excluding Sunday occurrences from the minimum-interval check.
    return (
      [
        [weeklyData.sunday, 0],
        [weeklyData.monday, 1],
        [weeklyData.tuesday, 2],
        [weeklyData.wednesday, 3],
        [weeklyData.thursday, 4],
        [weeklyData.friday, 5],
        [weeklyData.saturday, 6],
      ] as Array<[boolean, number]>
    )
      .filter(([isSelected]) => isSelected)
      .map(([, weekday]) => weekday);
  }

  return undefined;
};

const determineMinimumIntervalViolations = (
  entries: AutoUpdateTimeScheduleEntry[],
  { minimumIntervalMinutes = 0, selectedScheduleType }: TimeEntryValidationOptions = {},
): boolean[] => {
  const invalidEntries = entries.map(() => false);
  if (minimumIntervalMinutes <= 0 || entries.length < 2) {
    return invalidEntries;
  }

  const repeatingWeekdays = selectedScheduleType ? getRepeatingWeekdays(selectedScheduleType) : undefined;
  const occurrences = _.flatMap(entries, (entry, index) => {
    const entryMinutes = getEntryMinutes(entry);
    return repeatingWeekdays?.length
      ? repeatingWeekdays.map((weekday) => ({ index, minutes: weekday * MINUTES_PER_DAY + entryMinutes }))
      : [{ index, minutes: entryMinutes }];
  }).sort((a, b) => a.minutes - b.minutes);

  const markIfTooClose = (previous: (typeof occurrences)[number], next: (typeof occurrences)[number], gap: number) => {
    if (gap < minimumIntervalMinutes) {
      invalidEntries[previous.index] = true;
      invalidEntries[next.index] = true;
    }
  };

  occurrences.forEach((occurrence, index) => {
    const nextOccurrence = occurrences[index + 1];
    if (nextOccurrence) {
      markIfTooClose(occurrence, nextOccurrence, nextOccurrence.minutes - occurrence.minutes);
    }
  });

  if (repeatingWeekdays?.length && occurrences.length > 1) {
    const firstOccurrence = occurrences[0];
    const lastOccurrence = occurrences[occurrences.length - 1];
    markIfTooClose(
      lastOccurrence,
      firstOccurrence,
      firstOccurrence.minutes + MINUTES_PER_WEEK - lastOccurrence.minutes,
    );
  }

  return invalidEntries;
};

export const determineInvalidEntries = (
  entries: AutoUpdateTimeScheduleEntry[],
  validationOptions: TimeEntryValidationOptions = {},
): boolean[] => {
  // In future, account for different strategies too.
  const times = entries.map((entry) => entry.time);
  const minimumIntervalViolations = determineMinimumIntervalViolations(entries, validationOptions);

  // find duplicates
  return times.map((value, index) => {
    const firstFindIndex = times.indexOf(value);
    const lastFindIndex = times.lastIndexOf(value);

    return (firstFindIndex === lastFindIndex ? false : index !== firstFindIndex) || minimumIntervalViolations[index];
  });
};

export const areTimeEntriesValid = (
  entries: AutoUpdateTimeScheduleEntry[],
  validationOptions: TimeEntryValidationOptions = {},
) => {
  if (_.isEmpty(entries)) {
    return false;
  }

  return _.every(determineInvalidEntries(entries, validationOptions), (isInvalid) => !isInvalid);
};

/**
 * Determine whether the current schedule is valid
 *
 * @param updatedSchedule - The current schedule
 *
 * @returns true if the schedule is valid; false otherwise
 */
export const isScheduleValid = (
  updatedSchedule: UpdatedSchedule | undefined,
  validationOptions: TimeEntryValidationOptions = {},
): boolean => {
  if (!updatedSchedule) {
    return false;
  }

  const { irregularSchedule, selectedScheduleType, timeEntries } = updatedSchedule;
  if (_.isNil(selectedScheduleType) || !_.isNil(irregularSchedule)) {
    return false;
  }

  const { selectedType, data } = selectedScheduleType;
  const timeEntryValidationOptions = { ...validationOptions, selectedScheduleType };
  switch (selectedType) {
    case ScheduleTypeName.DAILY:
      return (
        isDailyScheduleValid(data[ScheduleTypeName.DAILY]) &&
        areTimeEntriesValid(timeEntries, timeEntryValidationOptions)
      );
    case ScheduleTypeName.WEEKLY:
      return (
        isWeeklyScheduleValid(data[ScheduleTypeName.WEEKLY]) &&
        areTimeEntriesValid(timeEntries, timeEntryValidationOptions)
      );
    case ScheduleTypeName.MONTHLY:
      return (
        isMonthlyScheduleValid(data[ScheduleTypeName.MONTHLY]) &&
        areTimeEntriesValid(timeEntries, timeEntryValidationOptions)
      );
    case ScheduleTypeName.LIVE: {
      const liveRateMinutes = getLiveRateMinutes(updatedSchedule.liveRate);
      return liveRateMinutes > 0 && liveRateMinutes >= (validationOptions.minimumIntervalMinutes ?? 0);
    }
    default:
      return false;
  }
};

export const getNewCronSchedule = ({ liveRate, selectedScheduleType, timeEntries }: UpdatedSchedule): string[] => {
  const { selectedType } = selectedScheduleType;
  if (selectedType !== ScheduleTypeName.LIVE) {
    return _.chain(timeEntries)
      .map((timeEntry) => entriesToQuartzCronExpression(selectedScheduleType, timeEntry))
      .compact()
      .value();
  }

  const rate: AutoRate = {
    value: liveRate.value,
    units: liveRate.unit.unit[0],
  };

  return [quartzCronExpressionHelper().rateToCronSchedule(rate)];
};
